import React, { useState } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../hooks/useAuth';
import { 
  Shield, 
  Eye, 
  Edit, 
  Trash2, 
  Archive, 
  RotateCcw, 
  Move, 
  Users, 
  Download, 
  Upload, 
  CheckCircle,
  XCircle,
  Building,
  Activity
} from 'lucide-react';

const UserPermissions: React.FC = () => {
  const { user } = useAuth();
  const permissions = usePermissions();
  const [showDetails, setShowDetails] = useState(false);

  const getPermissionIcon = (hasPermission: boolean) => {
    return hasPermission ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <XCircle className="h-4 w-4 text-red-500" />
    );
  };

  const getPermissionLabel = (hasPermission: boolean) => {
    return hasPermission ? 'Autorisé' : 'Interdit';
  };

  const getPermissionColor = (hasPermission: boolean) => {
    return hasPermission ? 'text-green-600' : 'text-red-600';
  };

  if (!user) {
    return null;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Shield className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Permissions Utilisateur
          </h3>
        </div>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          {showDetails ? 'Masquer' : 'Détails'}
        </button>
      </div>

      {/* Informations utilisateur */}
      <div className="mb-4 p-3 bg-gray-50 rounded-md">
        <div className="flex items-center space-x-2 mb-2">
          <Activity className="h-4 w-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-900">
            {user.name || user.email}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Shield className="h-4 w-4 text-gray-600" />
          <span className="text-sm text-gray-600">
            Rôle: {user.role?.toUpperCase() || 'NON DÉFINI'}
          </span>
        </div>
        {permissions.scopes && (
          <div className="mt-2">
            <div className="flex items-center space-x-2">
              <Building className="h-4 w-4 text-gray-600" />
              <span className="text-sm text-gray-600">
                Scopes: {permissions.scopes.sites?.length || 0} site(s), {permissions.scopes.buildings?.length || 0} bâtiment(s)
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Permissions de base */}
      <div className="space-y-3">
        <h4 className="text-md font-medium text-gray-900">Permissions de Base</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div className="flex items-center space-x-2">
              <Eye className="h-4 w-4 text-gray-600" />
              <span className="text-sm text-gray-700">Lecture</span>
            </div>
            <div className="flex items-center space-x-2">
              {getPermissionIcon(permissions.canView)}
              <span className={`text-sm ${getPermissionColor(permissions.canView)}`}>
                {getPermissionLabel(permissions.canView)}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div className="flex items-center space-x-2">
              <Edit className="h-4 w-4 text-gray-600" />
              <span className="text-sm text-gray-700">Création</span>
            </div>
            <div className="flex items-center space-x-2">
              {getPermissionIcon(permissions.canCreate)}
              <span className={`text-sm ${getPermissionColor(permissions.canCreate)}`}>
                {getPermissionLabel(permissions.canCreate)}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div className="flex items-center space-x-2">
              <Edit className="h-4 w-4 text-gray-600" />
              <span className="text-sm text-gray-700">Modification</span>
            </div>
            <div className="flex items-center space-x-2">
              {getPermissionIcon(permissions.canEdit)}
              <span className={`text-sm ${getPermissionColor(permissions.canEdit)}`}>
                {getPermissionLabel(permissions.canEdit)}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div className="flex items-center space-x-2">
              <Trash2 className="h-4 w-4 text-gray-600" />
              <span className="text-sm text-gray-700">Suppression</span>
            </div>
            <div className="flex items-center space-x-2">
              {getPermissionIcon(permissions.canDelete)}
              <span className={`text-sm ${getPermissionColor(permissions.canDelete)}`}>
                {getPermissionLabel(permissions.canDelete)}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div className="flex items-center space-x-2">
              <Archive className="h-4 w-4 text-gray-600" />
              <span className="text-sm text-gray-700">Archivage</span>
            </div>
            <div className="flex items-center space-x-2">
              {getPermissionIcon(permissions.canArchive)}
              <span className={`text-sm ${getPermissionColor(permissions.canArchive)}`}>
                {getPermissionLabel(permissions.canArchive)}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div className="flex items-center space-x-2">
              <RotateCcw className="h-4 w-4 text-gray-600" />
              <span className="text-sm text-gray-700">Restauration</span>
            </div>
            <div className="flex items-center space-x-2">
              {getPermissionIcon(permissions.canRestore)}
              <span className={`text-sm ${getPermissionColor(permissions.canRestore)}`}>
                {getPermissionLabel(permissions.canRestore)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Permissions avancées */}
      {showDetails && (
        <div className="mt-6 space-y-4">
          <h4 className="text-md font-medium text-gray-900">Permissions Avancées</h4>
          
          {/* Mouvements & Inventaires */}
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">Mouvements & Inventaires</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center space-x-2">
                  <Move className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-gray-700">Déplacement</span>
                </div>
                <div className="flex items-center space-x-2">
                  {getPermissionIcon(permissions.canMove)}
                  <span className={`text-sm ${getPermissionColor(permissions.canMove)}`}>
                    {getPermissionLabel(permissions.canMove)}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center space-x-2">
                  <Activity className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-gray-700">Sessions Inventaire</span>
                </div>
                <div className="flex items-center space-x-2">
                  {getPermissionIcon(permissions.canStartInventorySession)}
                  <span className={`text-sm ${getPermissionColor(permissions.canStartInventorySession)}`}>
                    {getPermissionLabel(permissions.canStartInventorySession)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions en Masse */}
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">Actions en Masse</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center space-x-2">
                  <Edit className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-gray-700">Modification en Masse</span>
                </div>
                <div className="flex items-center space-x-2">
                  {getPermissionIcon(permissions.canBulkEdit)}
                  <span className={`text-sm ${getPermissionColor(permissions.canBulkEdit)}`}>
                    {getPermissionLabel(permissions.canBulkEdit)}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center space-x-2">
                  <Trash2 className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-gray-700">Suppression en Masse</span>
                </div>
                <div className="flex items-center space-x-2">
                  {getPermissionIcon(permissions.canBulkDelete)}
                  <span className={`text-sm ${getPermissionColor(permissions.canBulkDelete)}`}>
                    {getPermissionLabel(permissions.canBulkDelete)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Import/Export */}
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">Import/Export</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center space-x-2">
                  <Download className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-gray-700">Export</span>
                </div>
                <div className="flex items-center space-x-2">
                  {getPermissionIcon(permissions.canExport)}
                  <span className={`text-sm ${getPermissionColor(permissions.canExport)}`}>
                    {getPermissionLabel(permissions.canExport)}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center space-x-2">
                  <Upload className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-gray-700">Import</span>
                </div>
                <div className="flex items-center space-x-2">
                  {getPermissionIcon(permissions.canImportCreate)}
                  <span className={`text-sm ${getPermissionColor(permissions.canImportCreate)}`}>
                    {getPermissionLabel(permissions.canImportCreate)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Administration */}
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">Administration</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-gray-700">Gestion Utilisateurs</span>
                </div>
                <div className="flex items-center space-x-2">
                  {getPermissionIcon(permissions.canManageUsers)}
                  <span className={`text-sm ${getPermissionColor(permissions.canManageUsers)}`}>
                    {getPermissionLabel(permissions.canManageUsers)}
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPermissions;
